<script setup>
import { ref, onMounted } from 'vue';
import { useConfiguracaoStore } from '@/stores/configuracao';
import { useNotificationStore } from '@/stores/notification';

// Initialize stores
const configuracaoStore = useConfiguracaoStore();
const notificationStore = useNotificationStore();

// Local state
const search = ref('');

const editDialogOpen = ref(false);
const currentVariavel = ref(null);
const editedValue = ref('');
const editedDescription = ref(''); 
const editLoading = ref(false);
const isCreateMode = ref(false); // New ref to track if we're creating a new config
const editedCodigo = ref(''); // New ref for the configuration code when creating

// Fetch data on mount
onMounted(async () => {
  await carregarVariaveis();
});

// Methods
const carregarVariaveis = async () => {
  try {
    await configuracaoStore.fetchConfiguracoes();
  } catch (error) {
    notificationStore.showNotification('Erro ao carregar variáveis de configuração', 'error');
  }
};

const abrirDialogEdicao = (variavel) => {
  isCreateMode.value = false;
  currentVariavel.value = { ...variavel };
  editedValue.value = variavel.conteudo;
  editedDescription.value = variavel.descricao;
  editDialogOpen.value = true;
};

// New function to open the dialog for creating a new configuration
const abrirDialogCriacao = () => {
  isCreateMode.value = true;
  currentVariavel.value = null;
  editedCodigo.value = '';
  editedValue.value = '';
  editedDescription.value = '';
  editDialogOpen.value = true;
};

const salvarVariavel = async () => {
  if (isCreateMode.value) {
    // Create a new configuration
    await criarNovaConfiguracao();
  } else if (currentVariavel.value) {
    // Update existing configuration
    await atualizarConfiguracao();
  }
};

// New function to create a configuration
const criarNovaConfiguracao = async () => {
  if (!editedCodigo.value || !editedValue.value || !editedDescription.value) {
    notificationStore.showNotification('Preencha todos os campos', 'error');
    return;
  }
  
  editLoading.value = true;
  
  try {
    const response = await configuracaoStore.createConfiguracao({
      codigo: editedCodigo.value,
      conteudo: editedValue.value,
      descricao: editedDescription.value
    });
    
    if (response && (response.success || !response.error)) {
      notificationStore.showNotification('Configuração criada com sucesso!', 'success');
      editDialogOpen.value = false;
      
      // Refresh the list to get updated data
      await carregarVariaveis();
    } else {
      notificationStore.showNotification(response?.message || 'Erro ao criar configuração', 'error');
    }
  } catch (error) {
    console.error('Erro ao criar configuração:', error);
    notificationStore.showNotification('Erro ao criar configuração', 'error');
  } finally {
    editLoading.value = false;
  }
};

// Renamed function to update a configuration
const atualizarConfiguracao = async () => {
  editLoading.value = true;
  
  try {
    // Update the API call to include the edited description
    const response = await configuracaoStore.updateConfiguracao(
      currentVariavel.value.codigo, 
      { 
        conteudo: editedValue.value,
        descricao: editedDescription.value // Use the edited description
      }
    );
    
    if (response && (response.success || !response.error)) {
      notificationStore.showNotification('Variável atualizada com sucesso!', 'success');
      editDialogOpen.value = false;
      
      // Refresh the list to get updated data
      await carregarVariaveis();
    } else {
      notificationStore.showNotification(response?.message || 'Erro ao atualizar variável', 'error');
    }
  } catch (error) {
    console.error('Erro ao atualizar variável:', error);
    notificationStore.showNotification('Erro ao atualizar variável', 'error');
  } finally {
    editLoading.value = false;
  }
};

const cancelarEdicao = () => {
  editDialogOpen.value = false;
  currentVariavel.value = null;
  editedValue.value = '';
  editedDescription.value = '';
  editedCodigo.value = ''; // Clear the codigo field
  isCreateMode.value = false; // Reset the mode
};
</script>

<template>
  <div>
    <v-row>
      <v-col cols="12" class="d-flex align-center justify-space-between pt-0">
        <h1 class="text-h4 mb-2">Variáveis de Configuração</h1>
        
        <!-- Add a button to create a new configuration -->
        <v-btn
          color="primary"
          prepend-icon="mdi-plus"
          @click="abrirDialogCriacao"
        >
          Nova Configuração
        </v-btn>
      </v-col>
    </v-row>
    
    <!-- Error and loading indicators -->
    <v-alert v-if="configuracaoStore.error" type="error" closable class="mb-4">
      {{ configuracaoStore.error }}
    </v-alert>
    
    <v-progress-linear v-if="configuracaoStore.loading" indeterminate color="primary"></v-progress-linear>
    
    <!-- Data table -->
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <div>Lista de Configurações</div>
        <v-text-field
          v-model="search"
          label="Buscar"
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          class="mb-0 ml-4"
          style="max-width: 300px"
          hide-details
        ></v-text-field>
      </v-card-title>
      
      <v-data-table
        :headers="[
          { title: 'ID', key: 'id', sortable: true },
          { title: 'Código', key: 'codigo', sortable: true },
          { title: 'Valor', key: 'conteudo', sortable: false },
          { title: 'Descrição', key: 'descricao', sortable: true },
          { title: 'Ações', key: 'actions', sortable: false, align: 'end' }
        ]"
        :items="configuracaoStore.configuracoes"
        :loading="configuracaoStore.loading"
        :search="search"
        class="elevation-1"
      >
      

        <template v-slot:item.actions="{ item }">
            <v-icon class="mr-3" 
            color="medium-emphasis" 
            icon="mdi-pencil" 
            size="large" 
            title="Editar Variável"
            @click="abrirDialogEdicao(item)"></v-icon>
        </template>
        
        <template v-slot:no-data>
          <div class="pa-4 text-center">
            <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-database-off</v-icon>
            <div class="text-subtitle-1">Nenhuma variável encontrada</div>
          </div>
        </template>
      </v-data-table>
    </v-card>
    
    <!-- Edit/Create Dialog -->
    <v-dialog v-model="editDialogOpen" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-h5 bg-primary text-white">
          <v-icon start icon="mdi-pencil" class="mr-2"></v-icon>
          {{ isCreateMode ? 'Nova Configuração' : 'Editar Variável' }}
        </v-card-title>
        
        <v-card-text class="pt-4">
          <v-form @submit.prevent="salvarVariavel">
            <div v-if="!isCreateMode" class="mb-2">
              <strong>Código:</strong> {{ currentVariavel?.codigo }}
            </div>
            
            <v-text-field
              v-if="isCreateMode"
              v-model="editedCodigo"
              label="Código"
              density="compact"
              variant="outlined"
              hide-details="auto"
              class="mb-3"
              autofocus
              required
              placeholder="Informe o código único da configuração"
            ></v-text-field>
            
            <v-text-field
              v-model="editedValue"
              label="Valor"
              density="compact"
              variant="outlined"
              hide-details="auto"
              class="mb-3"
              :autofocus="!isCreateMode"
              required
            ></v-text-field>
            
            <v-textarea
              v-model="editedDescription"
              label="Descrição"
              density="compact"
              variant="outlined"
              auto-grow
              rows="3"
              hide-details="auto"
              class="mb-3"
              required
            ></v-textarea>
            
            <div class="d-flex justify-end mt-4">
              <v-btn 
                color="grey-darken-1" 
                variant="text" 
                class="mr-2" 
                @click="cancelarEdicao"
                :disabled="editLoading"
              >
                Cancelar
              </v-btn>
              <v-btn 
                color="primary" 
                type="submit"
                :loading="editLoading"
                :disabled="editLoading"
              >
                {{ isCreateMode ? 'Criar' : 'Salvar' }}
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
/* Consistent styling with other views */
.v-expansion-panel-title {
  padding: 10px 15px;
}

.action-icon {
  margin-left: 6px;
  cursor: pointer;
  opacity: 0.8;
  transition: opacity 0.2s, transform 0.2s;
}

.action-icon:hover {
  opacity: 1;
  transform: scale(1.2);
}
</style>
